<template>
  <Modal
    :full-height="$isMobile"
    :modal-style="{ width: '410px' }"
    :title="$t('modals.exclude_mention_modal.title')"
    @close="handleMentionRemoval"
  >
    <div
      key="form"
      class="full-height q-pa-xs scroll column"
      :class="{ 'q-pa-lg': $isDesktop }"
    >
      <div key="form">
        <span class="full-width info block text-justify">
          {{ message }}
        </span>
        <div class="row q-ml-none q-mt-lg">
          <div
            v-for="reason in reasons"
            :key="reason.option"
            class="full-width"
          >
            <RadioCircle
              v-model="selectedReason"
              name="reason"
              :value="reason"
              @update:model-value="selectReason"
            >
              {{ reason.description }}
            </RadioCircle>
          </div>
          <div
            v-if="selectedReason === otherReason"
            class="full-width q-pa-sm"
          >
            <div class="row q-mb-smd">
              <textarea
                ref="comment"
                v-model="selectedReason.comment"
                class="full-width"
                :class="{
                  'border-error':
                    validated && submitted && !selectedReason.comment,
                }"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <template #buttons>
      <BaseButton
        color="white"
        :label="$t('global.cancel')"
        new-design
        @click="$emit('close')"
      />
      <BaseButton
        v-close-popup
        color="primary"
        :disabled="!selected"
        :label="$t('global.confirm')"
        new-design
        @click="
          submitted = true;
          handleSubmit();
        "
      />
    </template>
  </Modal>
</template>

<script>
import { storeToRefs } from "pinia";

import { BaseButton } from "shared/components/base";
import RadioCircle from "shared/components/core/forms/RadioCircle.vue";
import Modal from "shared/components/core/modals/Modal.vue";
import { removeMention } from "shared/services/stream";
import { useUniversalPlayerStore } from "shared/stores/universalPlayer";

export default {
  name: "ExcludeMentionModal",
  components: {
    Modal,
    RadioCircle,
    BaseButton,
  },
  props: {
    stream: {
      type: Object,
      required: true,
    },
    mention: {
      type: Object,
      default: () => ({}),
    },
    removedMentions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["close", "error", "removed"],
  setup() {
    const universalPlayer = useUniversalPlayerStore();

    const {
      playerHide,
      setPlayerPlaying,
      playerRemoveListItem,
      playerSetInitialClip,
    } = universalPlayer;

    const { universalPlayerRef, playerList, playerClip } =
      storeToRefs(universalPlayer);

    return {
      universalPlayerRef,
      playerList,
      playerClip,
      playerHide,
      setPlayerPlaying,
      playerRemoveListItem,
      playerSetInitialClip,
    };
  },
  data() {
    return {
      reasons: [
        {
          option: "A",
          description: this.$t("modals.exclude_mention_modal.reasons.A"),
          comment: "Source is irrelevant",
        },
        {
          option: "B",
          description: this.$t("modals.exclude_mention_modal.reasons.B"),
          comment: "Content is inaccurate or incorrect",
        },
        {
          option: "C",
          description: this.$t("modals.exclude_mention_modal.reasons.C"),
          comment: "Keyword is no longer relevant to the brief",
        },
        {
          option: "D",
          description: this.$t("modals.exclude_mention_modal.reasons.D"),
          comment: "Contains advertising material",
        },
        {
          option: "E",
          description: this.$t("modals.exclude_mention_modal.reasons.E"),
          comment: "",
        },
      ],
      selectedReason: { comment: "" },
      submitted: false,
      validated: false,
      success: false,
      selected: false,
    };
  },
  computed: {
    comment() {
      let comment = `${this.selectedReason.option} - ${this.selectedReason.comment}`;

      if (this.selectedReason === this.otherReason) {
        comment += ` - ${this.selectedReason.comment}`;
      }

      return comment;
    },
    otherReason() {
      return this.reasons[this.reasons.length - 1];
    },
    multiple() {
      return this.removedMentions.length > 1;
    },
    message() {
      if (this.multiple) {
        return this.$t("modals.exclude_mention_modal.message_multiple");
      }

      return this.$t("modals.exclude_mention_modal.message_single");
    },
  },
  methods: {
    selectReason() {
      this.selected = true;

      // must provide a comment if selecting other
      if (
        this.selectedReason === this.otherReason &&
        !this.selectedReason.comment
      ) {
        this.success = false;
        this.validated = true;
        this.focusInput();
      }
    },
    async handleSubmit() {
      if (
        this.selectedReason === this.otherReason &&
        !this.selectedReason.comment
      ) {
        this.success = false;
        this.validated = true;
        this.focusInput();

        return false;
      }

      await this.excludeMentions();
      this.success = true;
      this.handleMentionRemoval();

      return true;
    },
    async excludeMentions() {
      this.$q.loading.show({ delay: 0 });

      try {
        if (this.removedMentions.length) {
          await Promise.all(
            this.removedMentions.map((mention) => this.excludeMention(mention))
          );
        } else {
          await this.excludeMention(this.mention);
        }
      } catch (error) {
        this.$emit("error", error);
      } finally {
        this.$q.loading.hide();
      }
    },
    excludeMention(mention) {
      const mentionToRemove = { ...mention, comment: this.comment };

      this.$track("Excluded Mention", {
        stream_id: this.stream.id,
        stream_type: this.stream.locked ? "brief" : "stream",
        mention_id: mention.id,
        mention_type: mention.type,
        comment: this.comment,
      });

      if (this.playerList.find((player) => player.id === this.mention.id)) {
        this.stopUniversalPlayer();
      }

      return removeMention(this.stream, mentionToRemove);
    },
    stopUniversalPlayer() {
      this.playerRemoveListItem(this.mention);

      if (this.playerClip.id === this.mention.id) {
        this.pauseUniversalPlayer();
        this.playerSetInitialClip(this.playerList[0]);
      }

      if (!this.playerList.length) {
        this.playerHide();
      }
    },
    pauseUniversalPlayer() {
      this.universalPlayerRef.pause();
      this.setPlayerPlaying(false);
    },
    focusInput() {
      this.$nextTick(() => this.$refs.comment.focus());
    },
    handleMentionRemoval() {
      if (this.success) {
        this.$emit("removed", this.mention);
      }

      this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "shared/css/utils/transitions/fade";

:root {
  --fade-duration: 0.3s;
}

.border-error {
  border: 1px solid;
}

textarea {
  resize: none;
  border-radius: 2px;
  background-color: #f5f6f7;
  border: solid 0.5px #e8e8e8;
}
</style>
